// Estilos do cabeçalho e das ações da conversa (M5.2) da modernização do
// InfoChat.
//
// Função pura — sem JSX, sem I/O, sem leitura de localStorage/backend.
// Recebe o `theme` do MUI (idealmente já com `theme.infoChat` da Entrega
// M2) e devolve só objetos de estilo simples (CSS-in-JS), para serem
// mesclados nas classes já existentes de `TicketHeader`,
// `TicketHeaderSkeleton` e `TicketActionButtonsCustom`. Não aplica nada
// sozinha — quem decide onde/como usar é cada um desses três arquivos.
//
// Helper independente do `infoChatTicketsListStyles.js` (M5.1) — por
// decisão explícita, não importa nem se acopla a ele, mesmo definindo um
// `focusRingAccent` equivalente (duplicação intencional, cada entrega fica
// isolada).
//
// Escopo desta entrega (M5.2): só a apresentação do cabeçalho e da barra de
// ações da conversa — superfícies, bordas, cor dos ícones de ação, o toggle
// mobile e o anel de foco. Nenhuma rota, condição, handler, endpoint,
// payload ou estrutura JSX é decidida aqui.
//
// Usa exclusivamente os tokens de `theme.infoChat` para cor — nunca um hex
// literal novo aqui.

import { createInfoChatDesignTokens } from "../theme/infoChatDesignTokens";

const hasCompleteInfoChatTokens = tokens =>
  Boolean(
    tokens &&
      tokens.brand &&
      tokens.surfaces &&
      tokens.borders &&
      tokens.text &&
      tokens.shadows &&
      tokens.transitions &&
      tokens.radii &&
      tokens.spacing &&
      tokens.status
  );

// Mesmo padrão de fallback seguro já usado em infoChatNavigationStyles.js
// (M4), infoChatGlobalShellStyles.js (M3), infoChatLoginStyles.js
// (LOGIN-2) e infoChatTicketsListStyles.js (M5.1) — nunca deixa o
// cabeçalho/ações quebrar caso theme.infoChat esteja ausente/incompleto, e
// nunca diverge dos tokens reais por reaproveitar a mesma função pura da M2.
const resolveInfoChatTokens = theme => {
  if (hasCompleteInfoChatTokens(theme?.infoChat)) {
    return theme.infoChat;
  }
  return createInfoChatDesignTokens(theme?.palette?.type, theme?.palette?.primary?.main);
};

/**
 * Estilos do cabeçalho e das ações da conversa modernizados (M5.2).
 *
 * @param {object} theme - tema do MUI (idealmente já com `theme.infoChat`).
 */
export const createInfoChatTicketHeaderStyles = theme => {
  const tokens = resolveInfoChatTokens(theme);

  return {
    // ---- TicketHeader ----

    // Fundo do cabeçalho — antes `theme.palette.total` (#fff/#222 fixos em
    // App.js). Migrado para a superfície "base" do sistema de tokens, que
    // já cumpre o mesmo papel (fundo de cartão) nos dois modos.
    headerSurface: {
      backgroundColor: tokens.surfaces.base
    },
    // Borda inferior do cabeçalho — antes "rgba(0, 0, 0, 0.12)" fixo, sem
    // reagir ao modo escuro.
    headerBorder: {
      borderColor: tokens.borders.subtle
    },

    // ---- TicketHeaderSkeleton ----

    // Fundo do skeleton — antes `theme.palette.tabHeaderBackground`
    // (#EEE/#666 fixos em App.js). Migrado para a superfície "subtle", que
    // já é o tom levemente destacado do sistema de tokens (o skeleton some
    // e dá lugar ao `headerSurface` real assim que o carregamento termina).
    skeletonSurface: {
      backgroundColor: tokens.surfaces.subtle
    },

    // ---- TicketActionButtonsCustom ----

    // Cor dos ícones de ação (copiar telefone, respostas rápidas,
    // resolver, devolver à fila, transferir, vincular carteira) — antes
    // `theme.mode === "light" ? theme.palette.primary.main : "#FFF"`, que
    // no modo escuro virava branco plano. Cor institucional única, sem
    // ramificar por modo (mesmo raciocínio já aplicado aos botões de ação
    // da lista na M5.1).
    actionIconColor: {
      color: tokens.brand.primaryColor
    },
    // Cor dos mesmos ícones quando `disabled` (copiar telefone sem
    // número, vincular carteira durante o loading) — antes sem nenhuma
    // cor própria para o estado desabilitado (ficava só com a opacidade
    // padrão do MUI sobre a cor ativa).
    actionIconDisabledColor: {
      color: tokens.text.disabled
    },

    // Superfície/borda do toggle mobile ("Ações do atendimento ▼") — antes
    // background "#fff" e borda "#e5e5e5" fixos, sem reagir ao modo escuro.
    mobileToggleSurface: {
      backgroundColor: tokens.surfaces.base,
      borderColor: tokens.borders.subtle
    },
    // Texto/ícone do toggle mobile — antes "#0877b9" fixo (não é a cor
    // institucional dinâmica da empresa).
    mobileToggleText: {
      color: tokens.brand.primaryColor
    },
    // Hover do toggle mobile — estado novo (antes não existia nenhum
    // destaque de hover), na mesma cor institucional em tom sutil, seguindo
    // o padrão de alpha hex já usado na M5.1.
    mobileToggleHover: {
      backgroundColor: `${tokens.brand.primaryColor}0D`
    },

    // Anel de foco visível (navegação real por teclado) — mesma cor
    // institucional, mesmo raciocínio já corrigido em
    // infoChatNavigationStyles.js (M4) e infoChatTicketsListStyles.js
    // (M5.1): usa `outline`/`outlineOffset`, nunca `boxShadow` ou
    // `backgroundColor`, para nunca disputar a mesma propriedade usada
    // pelos estados de hover/superfície acima — o foco sempre aparece por
    // cima de qualquer combinação de estado, sem sobrescrever nada.
    focusRingAccent: {
      outline: `2px solid ${tokens.brand.primaryColor}`,
      outlineOffset: "-2px"
    }
  };
};

export default createInfoChatTicketHeaderStyles;
